import * as _ from 'lodash';
import { Component } from 'react';
import type { ReactElement } from 'react';

import { Alert, Button, ControlLabel, FormControl, FormGroup, Modal } from 'react-bootstrap';
import type { CurrentRawVuln } from '../../../models/types';
import { Icon, icons } from '../generic';
import {
  getNormalizedVersionRegex,
  getNonNormalVersionRegex,
  getIntervalRegex,
} from '../../../shared/newVersionResolvers/regexVersions';
import { getCVSSV2Regex, getCVSSV3Regex } from '../../../shared/vulns/cvssUtils';
import {
  buildEmptyErrorsArray,
  transformToFormData,
  transformToRawVulns,
  modalHasErrors,
  populateErrorsArray,
  setStatusField,
} from './RawVulnUtils';
import { CVEStatus, RawVulnStatus } from '../../../typings/enums';
import { hasUnboundUpperVersionRange } from '../../../shared/raw_vulns';
import { isValidCveIdentifier, isValidCWE } from '../../../shared/vulns';
import { isLinuxFetcher } from '../../../shared/fetchers';
import type {
  FieldEntry,
  RawVulnFormData,
  ModalErrorArray,
} from './RawVulnUtils';

type ModalReviewRawVulnProps = {
  rawVulnToEdit?: CurrentRawVuln[];
  show: boolean;
  onHide: () => void;
  onSave: (newVal: CurrentRawVuln[]) => void;
};

type ModalReviewRawVulnState = {
  rawVulnsToEdit: RawVulnFormData | null;
  errorMsgToDisplay: string | null;
  errors: ModalErrorArray;
};

type EditableField = {
  // if true, the field cannot be changed unless it is not defined
  isImmutable?: boolean;
  displayName: string;
  columnName: string;
  specialType?: string;
  isList?: boolean;
  options?: Array<{
    title: string;
    value: string;
  }>;
  isValid?: (value: string, rawVulnData?: RawVulnFormData) => boolean;
};

type IndexedEditableField = { [key in string]: EditableField };

export default class ModalReviewRawVuln extends Component<ModalReviewRawVulnProps, ModalReviewRawVulnState> {
  static versionValidation = (value: string, rawVulnData: RawVulnFormData): boolean => {
    // Handle null validation
    if (!value) return true;
    const fetcher = _.head(rawVulnData?.fetcher)?.value;

    // Don't validate linux for now
    if (isLinuxFetcher(fetcher)) return true;

    // Check interval notation first
    if (value.match(new RegExp(`^${getIntervalRegex().source}$`, 'ig'))) return true;

    const specs = value.split(',');
    if (specs.length > 2) return false; // Error if more than two versions ranges

    return !specs.some(
      (spec) =>
        !spec.match(getNormalizedVersionRegex()) && // Match for version ranges
        !spec.match(getNonNormalVersionRegex()),
    );
  };

  // eslint-disable-next-line react/sort-comp
  static editableFields: IndexedEditableField = {
    cve: {
      displayName: `CVE`,
      columnName: 'cve',
      isValid: (value) => !value || isValidCveIdentifier(value), // Match for CVE
      isImmutable: true,
    },
    cwes: {
      displayName: `CWE's`,
      columnName: 'cwes',
      isList: true,
      isValid: (value) => !value || isValidCWE(value),
    },
    affectedVersionRanges: {
      displayName: 'Affected Versions',
      columnName: 'affectedVersionRanges',
      isList: true,
      isValid: (value, formData) =>
        ModalReviewRawVuln.versionValidation(value, formData) && !hasUnboundUpperVersionRange([value]),
    },
    patchedVersionRanges: {
      displayName: 'Patched Versions',
      columnName: 'patchedVersionRanges',
      isList: true,
      isValid: ModalReviewRawVuln.versionValidation,
    },
    unaffectedVersionRanges: {
      displayName: 'Unaffected Versions',
      columnName: 'unaffectedVersionRanges',
      isList: true,
      isValid: ModalReviewRawVuln.versionValidation,
    },
    references: {
      displayName: 'References',
      columnName: 'references',
      isList: true,
    },
    authors: {
      displayName: 'Authors',
      columnName: 'authors',
      isList: true,
    },
    fetcher: {
      displayName: 'Fetcher',
      columnName: 'fetcher',
      isImmutable: true,
      isValid: (value) => !!value, // If null or default value
    },
    cvssV2Vector: {
      displayName: 'CVSS V2 Vector',
      columnName: 'cvssV2Vector',
      isValid: (value) => !value || !!value.match(getCVSSV2Regex()),
    },
    cvssV3Vector: {
      displayName: 'CVSS V3 Vector',
      columnName: 'cvssV3Vector',
      isValid: (value) => !value || !!value.match(getCVSSV3Regex()),
    },
    description: {
      displayName: 'Description',
      columnName: 'description',
      specialType: 'textarea',
    },
    nvdDescription: {
      displayName: 'NVD Description',
      columnName: 'nvdDescription',
      specialType: 'textarea',
    },
    cveStatus: {
      displayName: 'CVE Status',
      columnName: 'cveStatus',
      specialType: 'select',
      options: [
        {
          title: 'Disputed',
          value: CVEStatus.Disputed,
        },
        {
          title: 'Reserved',
          value: CVEStatus.Reserved,
        },
        {
          title: 'Rejected',
          value: CVEStatus.Rejected,
        },
        {
          title: 'In Review',
          value: CVEStatus.InReview,
        },
        {
          title: 'Completed',
          value: CVEStatus.Completed,
        },
      ],
    },
    notes: {
      displayName: 'Notes',
      columnName: 'notes',
      specialType: 'textarea',
    },
    status: {
      displayName: 'Review Status',
      columnName: 'status',
      specialType: 'select',
      options: [
        {
          title: 'Complete',
          value: 'complete',
        },
        {
          title: 'Priority',
          value: 'priority',
        },
        {
          title: 'Disabled',
          value: 'disabled',
        },
      ],
    },
  };

  constructor(props: ModalReviewRawVulnProps) {
    super(props);

    this.state = {
      rawVulnsToEdit: null,
      errorMsgToDisplay: null,
      errors: {},
    };
  }

  componentDidUpdate(prevProps: ModalReviewRawVulnProps): void {
    if (
      prevProps.show !== this.props.show ||
      (this.props.rawVulnToEdit && !_.isEqual(prevProps.rawVulnToEdit, this.props.rawVulnToEdit))
    ) {
      const rawVulnsToEdit = transformToFormData(_.cloneDeep(this.props.rawVulnToEdit));
      const errorsArr = buildEmptyErrorsArray(rawVulnsToEdit);

      rawVulnsToEdit.status = setStatusField(rawVulnsToEdit.status);

      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(
        {
          rawVulnsToEdit,
          errorMsgToDisplay: null,
          errors: errorsArr,
        },
        this.validateAllFields,
      );
    }
  }

  onAddListValue = (field: string, rowIndx: number): void => {
    this.setState((prevState) => {
      const newVuln = _.cloneDeep(prevState.rawVulnsToEdit);

      if (!newVuln[field][rowIndx]?.value) {
        newVuln[field][rowIndx].value = [];
      }
      newVuln[field][rowIndx].value.push('');

      const newErrors = _.cloneDeep(prevState.errors);
      if (!newErrors[field][rowIndx]) {
        newErrors[field][rowIndx] = [];
      }
      newErrors[field][rowIndx].push(true);

      return {
        ...prevState,
        rawVulnsToEdit: newVuln,
        errors: newErrors,
      };
    });
  };

  onUpdate = (field: string, rowIndx: number, newValue: string) => {
    const currField = ModalReviewRawVuln.editableFields[field];
    const isValid = currField.isValid ? currField.isValid(newValue, this.state.rawVulnsToEdit) : true;

    this.setState((prevState) => {
      const newVuln = _.cloneDeep(prevState.rawVulnsToEdit);
      newVuln[field][rowIndx].value = newValue;

      const newErrors = _.cloneDeep(prevState.errors);
      newErrors[field][rowIndx] = isValid;

      // Don't allow modification of CVE unless empty
      if (currField?.isImmutable && _.head(this.props.rawVulnToEdit)[field] !== null) {
        newVuln[field][rowIndx].value = _.head(this.props.rawVulnToEdit)[field];
        // ignore errors, they wont get pushed to state
        newErrors[field][rowIndx] = true;
      }

      return {
        ...prevState,
        rawVulnsToEdit: newVuln,
        errors: newErrors,
      };
    });
  };

  onUpdateListValue = (field: string, rowIndx: number, indx: number, newValue: string): void => {
    const currField = ModalReviewRawVuln.editableFields[field];
    const isValid = currField.isValid ? currField.isValid(newValue, this.state.rawVulnsToEdit) : true;

    this.setState((prevState) => {
      const newVuln = _.cloneDeep(prevState.rawVulnsToEdit);
      newVuln[field][rowIndx].value[indx] = newValue;

      const newErrors = _.cloneDeep(prevState.errors);
      newErrors[field][rowIndx][indx] = isValid;

      return {
        ...prevState,
        rawVulnsToEdit: newVuln,
        errors: newErrors,
      };
    });
  };

  onRemoveListValue = (field: string, rowIndx: number, listIndex: number): void => {
    this.setState((prevState) => {
      const newVuln = _.cloneDeep(prevState.rawVulnsToEdit);
      newVuln[field][rowIndx].value.splice(listIndex, 1);

      const newErrors = _.cloneDeep(prevState.errors);
      newErrors[field][rowIndx].splice(listIndex, 1);

      return {
        ...prevState,
        rawVulnsToEdit: newVuln,
        errors: newErrors,
      };
    });
  };

  onConfirm = (): void => {
    // Check for errors and display message if necessary
    const hasErrors = modalHasErrors(this.state.errors);
    let errorMsgToDisplay = hasErrors ? 'Please resolve highlighted issues' : null;

    this.setState((prevState) => ({
      ...prevState,
      errorMsgToDisplay,
    }));

    if (hasErrors) {
      return;
    }

    // _.compact any arrays before saving - helps to avoid saving blank list entries
    const sanitizedRawVuln = _.mapValues(this.state.rawVulnsToEdit, (field) =>
      field.map((val) => ({
        ...val,
        value: Array.isArray(val.value) ? _.compact(val.value) : val.value,
      })),
    );

    // Require notes if '*' is in affected versions
    if (
      sanitizedRawVuln.affectedVersionRanges.some((e) => e.value.includes('*')) &&
      !sanitizedRawVuln.notes?.some((note) => !_.isEmpty(note.value))
    ) {
      errorMsgToDisplay = 'Notes must be provided if all versions are affected.';
      this.setState((prevState) => ({
        ...prevState,
        errorMsgToDisplay,
      }));
      return;
    }

    // Since nested ternarys aren't allowed, this is functionally equivalent
    // Determine new status. Order of priority is: disabled -> priority -> complete
    let newStatus = sanitizedRawVuln.status.some((status) => status.value === RawVulnStatus.disabled)
      ? RawVulnStatus.disabled
      : RawVulnStatus.complete;
    newStatus = sanitizedRawVuln.status.some((status) => status.value === RawVulnStatus.priority)
      ? RawVulnStatus.priority
      : newStatus;

    if (
      newStatus === RawVulnStatus.disabled &&
      !sanitizedRawVuln.notes?.some((note) => !_.isEmpty(note.value))
    ) {
      errorMsgToDisplay = 'Notes must be provided when disabling a vulnerability.';
      this.setState((prevState) => ({
        ...prevState,
        errorMsgToDisplay,
      }));
      return;
    }

    sanitizedRawVuln.status = sanitizedRawVuln.status.map((field) => ({
      ...field,
      value: newStatus,
    })); // We can assume this has been reviewed

    // Todo - transform data back
    this.props.onSave(transformToRawVulns(sanitizedRawVuln, _.cloneDeep(this.props.rawVulnToEdit)));
    this.props.onHide();
  };

  validateAllFields = (): void => {
    // Check every field for errors
    this.setState((prevState) => {
      const newErrors = populateErrorsArray(prevState.rawVulnsToEdit, prevState.errors);

      // Check for errors and display message if necessary
      const errorMsgToDisplay = modalHasErrors(newErrors) ? 'Please resolve highlighted issues' : null;

      return {
        ...prevState,
        errorMsgToDisplay,
        errors: newErrors,
      };
    });
  };

  isReviewStatus = (val: string): boolean => val === RawVulnStatus.review || val === RawVulnStatus.priority;

  getListField = (field: string, rowIndx: number, listValues: string[]) => (
    <div style={{ border: '1px solid black', padding: '5px' }}>
      {(listValues || []).map((val, indx) => {
        const currField = ModalReviewRawVuln.editableFields[field];
        const errorsList = this.state.errors[currField.columnName][rowIndx] || [];

        return (
          <div key={field} className="row" style={{ marginBottom: '15px' }}>
            <div className="col-md-9">
              <input
                type="text"
                className={`form-control input-search ${!errorsList[indx] ? ' has-error' : ''}`}
                onChange={(ev) => this.onUpdateListValue(field, rowIndx, indx, ev.target.value)}
                value={val || ''}
              />
            </div>
            <div className="col-md-3">
              <Button onClick={() => this.onRemoveListValue(field, rowIndx, indx)}>
                <Icon icon={icons.minus} />
              </Button>
            </div>
          </div>
        );
      })}
      <Button onClick={() => this.onAddListValue(field, rowIndx)}>
        <span>
          <Icon icon={icons.plus} /> Add
        </span>
      </Button>
    </div>
  );

  renderEditableTable = () => (
    <div>
      {Object.keys(ModalReviewRawVuln.editableFields).map((field: string) => {
        // Iterate through fields
        const currField = ModalReviewRawVuln.editableFields[field];
        const currFieldEntries = this.state.rawVulnsToEdit[currField.columnName];

        const editableFields = currFieldEntries?.map((currFieldEntry: FieldEntry, rowIndx: number) => {
          // Iterate through individual entries within a field
          let editableField;
          let label;

          // If there are more than one field entries, add a label to the UI to show where that data is coming from
          if (currFieldEntries.length > 1) {
            label = <span>{currFieldEntry.source}</span>;
          }
          const isDisabled = !!ModalReviewRawVuln.editableFields[field]?.isImmutable;
          if (currField.isList) {
            editableField = this.getListField(currField.columnName, rowIndx, currFieldEntry.value);
          } else {
            const hasError = !this.state.errors[currField.columnName][rowIndx] ? ' has-error' : '';
            editableField = (
              <FormControl
                className={`form-control ${hasError}`}
                componentClass={currField.specialType || 'input'}
                value={currFieldEntry.value || ''}
                name={field}
                onChange={(ev) => this.onUpdate(field, rowIndx, ev.target.value)}
                disabled={isDisabled}
              >
                {currField.options &&
                  currField.options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.title}
                    </option>
                  ))}
              </FormControl>
            );
          }

          return (
            <div key={field}>
              {label}
              {editableField}
            </div>
          );
        });

        return (
          <div key={currField.columnName}>
            <FormGroup>
              <ControlLabel>{currField.displayName}</ControlLabel>
              {editableFields}
            </FormGroup>
          </div>
        );
      })}
    </div>
  );

  getStatusContent = (status: keyof typeof RawVulnStatus, isResolved: boolean) => {
    const messages = {
      [RawVulnStatus.priority]: <span style={{ color: 'rgba(225, 30, 30, 0.8)' }}>Priority Review</span>,
      [RawVulnStatus.review]: <span style={{ color: 'rgba(225, 30, 30, 0.8)' }}>Requires Review</span>,
      [RawVulnStatus.new]: <span>New Vulnerability</span>,
      [RawVulnStatus.complete]: <span>Complete</span>,
      [RawVulnStatus.disabled]: <span>Disabled</span>,
    };

    const statusContent: ReactElement[] = [messages[status as keyof typeof RawVulnStatus]];
    if (isResolved === false) {
      statusContent.push(<br />);
      statusContent.push(<span style={{ color: 'rgba(225, 30, 30, 0.8)' }}>Resolution Issue</span>);
    }
    return statusContent;
  };

  render() {
    const { show, onHide } = this.props;
    return (
      <Modal bsSize="large" show={show} onHide={onHide} backdrop="static" id="raw-vulns-modal">
        <div style={{ fontSize: '15px', position: 'absolute', padding: '25px' }}>
          {this.getStatusContent(
            _.head(this.state.rawVulnsToEdit?.status)?.value,
            _.head(this.state.rawVulnsToEdit?.isResolved)?.value,
          )}
        </div>
        <Modal.Header closeButton>
          <Modal.Title style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.5em' }}>
            Editing vuln for {_.head(this.state.rawVulnsToEdit?.projectName)?.value}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {this.state.errorMsgToDisplay && (
            <Alert style={{ backgroundColor: 'rgba(225, 30, 30, 0.8)' }}>
              {this.state.errorMsgToDisplay}
            </Alert>
          )}
          {this.state.rawVulnsToEdit ? this.renderEditableTable() : <span>Loading...</span>}
        </Modal.Body>
        <Modal.Footer>
          <Button color="primary" onClick={this.onConfirm}>
            Confirm Changes
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}
